/**
 * Success Stories Hook - Fetching and managing success story data
 * @file src/web/features/achievements/hooks/useSuccessStories.ts
 */

import { useState, useEffect } from 'react';
import { config } from '../../auth/config';

export interface SuccessStory {
  id: number;
  name: string;
  board: string;
  score: string;
  year: number;
  quote?: string | null;
  currentSchool?: string | null;
  imageKey?: string | null;
  imageUrl?: string | null;
  imageUrlExpiry?: string | null;
  order: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export const useSuccessStories = (includeInactive = false) => {
  const [stories, setStories] = useState<SuccessStory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStories = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');
      const endpoint = includeInactive ? '/api/achievements/admin' : '/api/achievements';
      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      };

      if (includeInactive && token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${config.api.baseUrl}${endpoint}`, {
        headers
      });

      if (!response.ok) {
        throw new Error('Failed to fetch success stories');
      }

      const data = await response.json();
      setStories(data.stories || []);
    } catch (err: any) {
      setError(err.message);
      console.error('Error fetching success stories:', err);
    } finally {
      setLoading(false);
    }
  };

  const createStory = async (storyData: Partial<SuccessStory>) => {
    const token = localStorage.getItem('auth_token');
    if (!token) throw new Error('Authentication required');

    const response = await fetch(`${config.api.baseUrl}/api/achievements`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(storyData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create success story');
    }

    const data = await response.json();
    setStories(prev => [...prev, data.story]);
    return data.story;
  };

  const updateStory = async (id: number, storyData: Partial<SuccessStory>) => {
    const token = localStorage.getItem('auth_token');
    if (!token) throw new Error('Authentication required');

    const response = await fetch(`${config.api.baseUrl}/api/achievements/${id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(storyData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to update success story');
    }

    const data = await response.json();
    setStories(prev => prev.map(s => s.id === id ? data.story : s));
    return data.story;
  };

  const deleteStory = async (id: number) => {
    const token = localStorage.getItem('auth_token');
    if (!token) throw new Error('Authentication required');

    const response = await fetch(`${config.api.baseUrl}/api/achievements/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to delete success story');
    }

    setStories(prev => prev.filter(s => s.id !== id));
  };

  const uploadImage = async (id: number, file: File) => {
    const token = localStorage.getItem('auth_token');
    if (!token) throw new Error('Authentication required');

    const formData = new FormData();
    formData.append('image', file);

    const response = await fetch(`${config.api.baseUrl}/api/achievements/${id}/image`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to upload image');
    }

    const data = await response.json();
    setStories(prev => prev.map(s => s.id === id ? data.story : s));
    return data.story;
  };

  const deleteImage = async (id: number) => {
    const token = localStorage.getItem('auth_token');
    if (!token) throw new Error('Authentication required');

    const response = await fetch(`${config.api.baseUrl}/api/achievements/${id}/image`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to delete image');
    }

    const data = await response.json();
    setStories(prev => prev.map(s => s.id === id ? data.story : s));
    return data.story;
  };

  useEffect(() => {
    fetchStories();
  }, [includeInactive]);

  return {
    stories,
    loading,
    error,
    refetch: fetchStories,
    createStory,
    updateStory,
    deleteStory,
    uploadImage,
    deleteImage
  };
};

export const useSuccessStory = (id: number) => {
  const [story, setStory] = useState<SuccessStory | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStory = async () => {
    if (!id) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${config.api.baseUrl}/api/achievements/${id}`);

      if (!response.ok) {
        throw new Error('Failed to fetch success story');
      }

      const data = await response.json();
      setStory(data.story);
    } catch (err: any) {
      setError(err.message);
      console.error('Error fetching success story:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStory();
  }, [id]);

  return {
    story,
    loading,
    error,
    refetch: fetchStory
  };
};
